/**
 * Why an area's counted score is lower than its own checks add up to.
 *
 * A check that does not pass is capped at its status score, so an Income card whose
 * checks add up to 100 can count as 50. The card chip, the "How this score was
 * calculated" table and the summary's domain chip all explain that gap with these
 * helpers, so a reviewer reads the same reason, in the same words, wherever they see
 * the number. Mirrors the backend's `_status_reason` in combined.py.
 */

/** The cap's reason, worded for the check's status. */
export function capReason(status: string | null | undefined): string {
  const s = (status ?? '').toUpperCase();
  if (s === 'FAIL') return 'a failed check earns no points';
  if (s === 'UNAVAILABLE' || s === 'ERROR') return 'a check that could not be completed cannot earn full marks';
  return 'a check that needs review cannot earn full marks';
}

/**
 * True only when the two numbers a reviewer would SEE differ. The backend flags a cap
 * on the unrounded values, so 50.3 capped to 50 would otherwise read
 * "50 (capped from 50)".
 */
export function isVisiblyCapped(cardScore: number | null | undefined, counted: number | null | undefined): boolean {
  return cardScore != null && counted != null && Math.round(cardScore) > Math.round(counted);
}

function listing(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/** "Payroll source and Business registration need review" — which checks held the card. */
export function capCause(capChecks: readonly string[] | null | undefined, status: string | null | undefined): string | null {
  const checks = (capChecks ?? []).filter(Boolean);
  if (!checks.length) return null;
  const s = (status ?? '').toUpperCase();
  const verb =
    s === 'FAIL'
      ? 'failed'
      : s === 'UNAVAILABLE' || s === 'ERROR'
        ? 'could not be completed'
        : checks.length === 1 ? 'needs review' : 'need review';
  return `${listing(checks)} ${verb}`;
}

/** One sentence for a tooltip: both numbers, the rule, and which checks caused it. */
export function capExplanation(
  label: string,
  cardScore: number,
  counted: number,
  status: string | null | undefined,
  capChecks?: readonly string[] | null,
): string {
  const base = `${label} scored ${Math.round(cardScore)} on its own checks but counts as ${Math.round(counted)}, because ${capReason(status)}.`;
  const cause = capCause(capChecks, status);
  return cause ? `${base} ${cause.charAt(0).toUpperCase()}${cause.slice(1)}.` : base;
}

/** Label for the cap row in a score breakdown table — names the checks when known. */
export function capRowLabel(status: string | null | undefined, capChecks?: readonly string[] | null): string {
  const cause = capCause(capChecks, status);
  return cause ? `Capped — ${cause}` : `Capped — ${capReason(status)}`;
}
